import React, { useState, useEffect } from 'react';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Select, { SelectItem } from '../ui/Select';
import { FaMapMarkerAlt, FaClock, FaCar, FaShieldAlt, FaUserTie, FaWifi, FaBaby, FaSuitcase, FaEnvelope, FaPhone, FaUser } from 'react-icons/fa';
import { MdEventAvailable } from 'react-icons/md';

const RentalBooking = () => {
  const [selectedCar, setSelectedCar] = useState('');
  const [hours, setHours] = useState('');
  const [pickupLocation, setPickupLocation] = useState('');
  const [destination, setDestination] = useState('');
  const [tripType, setTripType] = useState(''); // 'inbound' or 'outstation'
  const [bookingType, setBookingType] = useState('instant'); // 'instant' or 'scheduled'
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');
  const [addons, setAddons] = useState({
    escort: false,
    wifi: false,
    childSeat: false,
    extraLuggage: false,
  });
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [priceBreakdown, setPriceBreakdown] = useState(null);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [bookingSuccess, setBookingSuccess] = useState(false);
  const [bookingNumber, setBookingNumber] = useState('');
  
  const MIN_HOURS = 2; // Minimum hours set by admin
  const BASE_HOURLY_RATE = 50; // Base rate per hour (can be fetched from admin settings)
  
  // Pricing structure
  const PRICING = {
    inbound: { base: BASE_HOURLY_RATE, multiplier: 1 },
    outstation: { base: BASE_HOURLY_RATE * 1.5, multiplier: 1.2 },
    addons: {
      escort: 25, // per hour
      wifi: 10, // per hour
      childSeat: 15, // one-time
      extraLuggage: 20, // one-time
    },
    scheduled: 0, // No extra charge for scheduled
    instant: 0, // No extra charge for instant
  };

  useEffect(() => {
    const fetchCars = async () => {
      try {
        const response = await fetch('/api/listing');
        if (!response.ok) {
          throw new Error(`API error: ${response.status}`);
        }
        const data = await response.json();
        // Ensure data is an array
        if (!Array.isArray(data)) {
          console.error('API returned non-array:', data);
          setCars([]);
          setLoading(false);
          return;
        }
        const activeCars = data.filter(car => car.visibility === "Active");
        setCars(activeCars);
        setLoading(false);
      } catch (error) {
        setCars([]);
        setLoading(false);
      }
    };
    fetchCars();
  }, []);

  const calculatePrice = () => {
    if (!selectedCar || !hours || !pickupLocation || !destination || !tripType) {
      setError('Please fill in all required fields.');
      return;
    }

    const hoursNum = parseFloat(hours);
    if (isNaN(hoursNum) || hoursNum < MIN_HOURS) {
      setError(`Minimum booking is ${MIN_HOURS} hours.`);
      return;
    }

    setError('');

    const selectedCarData = cars.find(car => car._id === selectedCar);
    if (!selectedCarData) {
      setError('Selected car not found.');
      return;
    }

    // Base calculation
    const tripPricing = PRICING[tripType] || PRICING.inbound;
    const basePrice = tripPricing.base * hoursNum;
    
    // Car premium (luxury cars cost more)
    const carPrice = parseFloat(selectedCarData.price?.replace(/,/g, '') || 0);
    const carMultiplier = carPrice > 100000 ? 1.5 : carPrice > 50000 ? 1.2 : 1.0;
    const adjustedBasePrice = basePrice * carMultiplier;

    // Add-ons calculation
    let addonsTotal = 0;
    if (addons.escort) addonsTotal += PRICING.addons.escort * hoursNum;
    if (addons.wifi) addonsTotal += PRICING.addons.wifi * hoursNum;
    if (addons.childSeat) addonsTotal += PRICING.addons.childSeat;
    if (addons.extraLuggage) addonsTotal += PRICING.addons.extraLuggage;

    // Outstation distance calculation (simplified - can be enhanced with actual distance API)
    let distanceCharge = 0;
    if (tripType === 'outstation' && pickupLocation && destination) {
      // Rough estimate: $0.5 per km (this would normally use a distance API)
      distanceCharge = 100; // Placeholder - should calculate actual distance
    }

    const subtotal = adjustedBasePrice + addonsTotal + distanceCharge;
    const serviceFee = subtotal * 0.1; // 10% service fee
    const total = subtotal + serviceFee;

    setPriceBreakdown({
      basePrice: adjustedBasePrice.toFixed(2),
      addonsTotal: addonsTotal.toFixed(2),
      distanceCharge: distanceCharge.toFixed(2),
      serviceFee: serviceFee.toFixed(2),
      total: total.toFixed(2),
      hours: hoursNum,
      car: selectedCarData.title,
    });
  };

  const handleAddonChange = (addon) => {
    setAddons(prev => ({
      ...prev,
      [addon]: !prev[addon]
    }));
  };

  const submitBooking = async () => {
    if (!name || !email || !phone) {
      setError('Please fill in all contact information fields.');
      return;
    }

    if (!priceBreakdown) {
      setError('Please calculate price first.');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      const selectedCarData = cars.find(car => car._id === selectedCar);

      const bookingData = {
        name,
        email,
        phone,
        carId: selectedCar,
        carName: selectedCarData?.title || '',
        hours: parseFloat(hours),
        pickupLocation,
        destination,
        tripType,
        bookingType,
        scheduledDate: bookingType === 'scheduled' ? scheduledDate : null,
        scheduledTime: bookingType === 'scheduled' ? scheduledTime : null,
        addons,
        pricing: {
          basePrice: priceBreakdown.basePrice,
          addonsTotal: priceBreakdown.addonsTotal,
          distanceCharge: priceBreakdown.distanceCharge,
          serviceFee: priceBreakdown.serviceFee,
          total: priceBreakdown.total,
        },
      };

      const response = await fetch('/api/bookings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(bookingData),
      });

      if (!response.ok) {
        await response.text();
        throw new Error('Failed to submit booking');
      }

      const result = await response.json();
      setBookingNumber(result.bookingNumber);
      setBookingSuccess(true);

      // Reset form
      setTimeout(() => {
        setSelectedCar('');
        setHours('');
        setPickupLocation('');
        setDestination('');
        setTripType('');
        setBookingType('instant');
        setScheduledDate('');
        setScheduledTime('');
        setAddons({
          escort: false,
          wifi: false,
          childSeat: false,
          extraLuggage: false,
        });
        setName('');
        setEmail('');
        setPhone('');
        setPriceBreakdown(null);
        setBookingSuccess(false);
      }, 10000); // Reset after 10 seconds
    } catch (error) {
      setError('Failed to submit booking. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-xl mx-auto p-6 md:p-8 bg-gradient-to-br from-white via-white to-gray-50 rounded-2xl shadow-2xl border border-gray-200 hover:shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)] transition-shadow duration-300 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-100/30 to-transparent rounded-bl-full"></div>
      <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-blue-100/30 to-transparent rounded-tr-full"></div>
      
      <div className="relative z-10 mb-6">
        <div className="flex items-center gap-2 mb-2">
          <div className="h-1 w-8 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
          <span className="text-xs font-semibold text-[#DC2626] uppercase tracking-wide">Book Now</span>
        </div>
        <h2 className="font-display text-2xl md:text-3xl font-bold text-gray-900 mb-2">Car Rental Booking</h2>
        <p className="text-sm md:text-base text-gray-600">Book luxury cars by the hour with premium service</p>
      </div>

      <div className="relative z-10 flex flex-col gap-5">
        {/* Car Selection */}
        <div>
          <label className="block text-sm font-semibold text-gray-900 mb-2">Select Car</label>
          <Select
            placeholder="Choose your vehicle"
            selectedKeys={selectedCar ? new Set([selectedCar]) : new Set()}
            onSelectionChange={(keys) => {
              const selected = Array.from(keys)[0] || '';
              setSelectedCar(selected);
            }}
            className="w-full"
          >
            {loading ? (
              <SelectItem value="loading">Loading...</SelectItem>
            ) : (
              cars.map((car) => (
                <SelectItem key={car._id} value={car._id}>
                  {car.title} ({car.make} {car.model})
                </SelectItem>
              ))
            )}
          </Select>
        </div>

        {/* Hours and Trip Type in Grid */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Hours (Min: {MIN_HOURS})</label>
            <Input
              type="number"
              placeholder={`Min ${MIN_HOURS}`}
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              min={MIN_HOURS}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Trip Type</label>
            <Select
              placeholder="Select"
              selectedKeys={tripType ? new Set([tripType]) : new Set()}
              onSelectionChange={(keys) => {
                const selected = Array.from(keys)[0] || '';
                setTripType(selected);
              }}
              className="w-full"
            >
              <SelectItem key="inbound" value="Inbound">Inbound</SelectItem>
              <SelectItem key="outstation" value="Outstation">Outstation</SelectItem>
            </Select>
          </div>
        </div>

        {/* Pickup and Destination in Grid */}
        <div className="grid grid-cols-1 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Pickup Location</label>
            <Input
              type="text"
              placeholder="Enter pickup address"
              value={pickupLocation}
              onChange={(e) => setPickupLocation(e.target.value)}
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-900 mb-2">Destination</label>
            <Input
              type="text"
              placeholder="Enter destination address"
              value={destination}
              onChange={(e) => setDestination(e.target.value)}
              className="w-full"
            />
          </div>
        </div>

        {/* Booking Type */}
        <div>
          <label className="text-sm font-semibold text-gray-900 mb-3 block">Booking Type</label>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setBookingType('instant')}
              className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all duration-300 text-sm font-medium transform hover:scale-105 active:scale-95 ${
                bookingType === 'instant'
                  ? 'border-[#DC2626] bg-gradient-to-br from-red-50 to-red-100 text-[#DC2626] shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-[#DC2626]/50 hover:bg-gray-50 shadow-sm hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <FaClock className={`text-sm ${bookingType === 'instant' ? 'text-[#DC2626]' : 'text-gray-500'}`} />
                <span>Instant</span>
              </div>
            </button>
            <button
              type="button"
              onClick={() => setBookingType('scheduled')}
              className={`flex-1 px-4 py-3 rounded-xl border-2 transition-all duration-300 text-sm font-medium transform hover:scale-105 active:scale-95 ${
                bookingType === 'scheduled'
                  ? 'border-[#DC2626] bg-gradient-to-br from-red-50 to-red-100 text-[#DC2626] shadow-md'
                  : 'border-gray-200 bg-white text-gray-700 hover:border-[#DC2626]/50 hover:bg-gray-50 shadow-sm hover:shadow-md'
              }`}
            >
              <div className="flex items-center justify-center gap-2">
                <MdEventAvailable className={`text-sm ${bookingType === 'scheduled' ? 'text-[#DC2626]' : 'text-gray-500'}`} />
                <span>Scheduled</span>
              </div>
            </button>
          </div>
        </div>

        {/* Scheduled Date & Time */}
        {bookingType === 'scheduled' && (
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Date</label>
              <Input
                type="date"
                value={scheduledDate}
                onChange={(e) => setScheduledDate(e.target.value)}
                min={new Date().toISOString().split('T')[0]}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-900 mb-2">Time</label>
              <Input
                type="time"
                value={scheduledTime}
                onChange={(e) => setScheduledTime(e.target.value)}
                className="w-full"
              />
            </div>
          </div>
        )}

        {/* Add-ons */}
        <div>
          <label className="text-sm font-semibold text-gray-900 mb-3 block">Add-ons <span className="text-gray-500 font-normal">(Optional)</span></label>
          <div className="grid grid-cols-2 gap-3">
            <div
              onClick={() => handleAddonChange('escort')}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                addons.escort
                  ? 'border-[#DC2626] bg-gradient-to-br from-red-50 to-red-100 shadow-md'
                  : 'border-gray-200 bg-white hover:border-[#DC2626]/50 hover:bg-gray-50 shadow-sm hover:shadow-md'
              }`}
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <div className={`p-3 rounded-xl transition-all duration-300 ${
                  addons.escort 
                    ? 'bg-gradient-to-br from-[#DC2626] to-[#B91C1C] text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                }`}>
                  <FaUserTie className="text-base" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">Escort</p>
                  <p className="text-xs text-gray-500 mt-0.5">${PRICING.addons.escort}/hr</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleAddonChange('wifi')}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                addons.wifi
                  ? 'border-[#DC2626] bg-gradient-to-br from-red-50 to-red-100 shadow-md'
                  : 'border-gray-200 bg-white hover:border-[#DC2626]/50 hover:bg-gray-50 shadow-sm hover:shadow-md'
              }`}
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <div className={`p-3 rounded-xl transition-all duration-300 ${
                  addons.wifi 
                    ? 'bg-gradient-to-br from-[#DC2626] to-[#B91C1C] text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                }`}>
                  <FaWifi className="text-base" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">WiFi</p>
                  <p className="text-xs text-gray-500 mt-0.5">${PRICING.addons.wifi}/hr</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleAddonChange('childSeat')}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                addons.childSeat
                  ? 'border-[#DC2626] bg-gradient-to-br from-red-50 to-red-100 shadow-md'
                  : 'border-gray-200 bg-white hover:border-[#DC2626]/50 hover:bg-gray-50 shadow-sm hover:shadow-md'
              }`}
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <div className={`p-3 rounded-xl transition-all duration-300 ${
                  addons.childSeat 
                    ? 'bg-gradient-to-br from-[#DC2626] to-[#B91C1C] text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                }`}>
                  <FaBaby className="text-base" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">Child Seat</p>
                  <p className="text-xs text-gray-500 mt-0.5">${PRICING.addons.childSeat}</p>
                </div>
              </div>
            </div>

            <div
              onClick={() => handleAddonChange('extraLuggage')}
              className={`p-4 rounded-xl border-2 cursor-pointer transition-all duration-300 transform hover:scale-105 ${
                addons.extraLuggage
                  ? 'border-[#DC2626] bg-gradient-to-br from-red-50 to-red-100 shadow-md'
                  : 'border-gray-200 bg-white hover:border-[#DC2626]/50 hover:bg-gray-50 shadow-sm hover:shadow-md'
              }`}
            >
              <div className="flex flex-col items-center gap-2 text-center">
                <div className={`p-3 rounded-xl transition-all duration-300 ${
                  addons.extraLuggage 
                    ? 'bg-gradient-to-br from-[#DC2626] to-[#B91C1C] text-white shadow-lg' 
                    : 'bg-gray-100 text-gray-600 group-hover:bg-gray-200'
                }`}>
                  <FaSuitcase className="text-base" />
                </div>
                <div>
                  <p className="font-semibold text-sm text-gray-900">Luggage</p>
                  <p className="text-xs text-gray-500 mt-0.5">${PRICING.addons.extraLuggage}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        <Button
          onClick={calculatePrice}
          className="w-full bg-gradient-to-r from-[#DC2626] to-[#B91C1C] hover:from-[#B91C1C] hover:to-[#991B1B] text-white font-semibold py-6 text-base transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.02] active:scale-[0.98]"
        >
          Calculate Price
        </Button>

        {/* Price Breakdown */}
        {priceBreakdown && (
          <div className="mt-6 p-6 bg-gradient-to-br from-red-50 via-pink-50 to-orange-50 rounded-2xl shadow-xl border-2 border-red-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-red-200/20 to-transparent rounded-bl-full"></div>
            <div className="relative z-10">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-1 w-8 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-full"></div>
                <h3 className="text-xl font-bold text-gray-900">Booking Summary</h3>
              </div>
            <div className="space-y-3 mb-4">
              <div className="flex justify-between items-center p-3 bg-white/80 backdrop-blur-sm rounded-xl text-sm border border-white/50 shadow-sm">
                <span className="text-gray-600 font-medium">Car:</span>
                <span className="font-semibold text-gray-900 text-xs text-right max-w-[60%] truncate">{priceBreakdown.car}</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/80 backdrop-blur-sm rounded-xl text-sm border border-white/50 shadow-sm">
                <span className="text-gray-600 font-medium">Duration:</span>
                <span className="font-semibold text-gray-900">{priceBreakdown.hours} hrs</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-white/80 backdrop-blur-sm rounded-xl text-sm border border-white/50 shadow-sm">
                <span className="text-gray-600 font-medium">Base Price:</span>
                <span className="font-semibold text-gray-900">${priceBreakdown.basePrice}</span>
              </div>
              {parseFloat(priceBreakdown.addonsTotal) > 0 && (
                <div className="flex justify-between items-center p-3 bg-white/80 backdrop-blur-sm rounded-xl text-sm border border-white/50 shadow-sm">
                  <span className="text-gray-600 font-medium">Add-ons:</span>
                  <span className="font-semibold text-gray-900">${priceBreakdown.addonsTotal}</span>
                </div>
              )}
              <div className="flex justify-between items-center p-3 bg-white/80 backdrop-blur-sm rounded-xl text-sm border border-white/50 shadow-sm">
                <span className="text-gray-600 font-medium">Service Fee:</span>
                <span className="font-semibold text-gray-900">${priceBreakdown.serviceFee}</span>
              </div>
            </div>
            <div className="border-t-2 border-red-200/50 pt-4">
              <div className="flex justify-between items-center p-4 bg-gradient-to-r from-[#DC2626] to-[#B91C1C] rounded-xl shadow-lg">
                <span className="text-white font-bold text-lg">Total:</span>
                <span className="text-2xl font-bold text-white">${priceBreakdown.total}</span>
              </div>
            </div>
            
            {/* Contact Information Form */}
            {!bookingSuccess ? (
              <div className="mt-4 space-y-3">
                <h4 className="font-semibold text-sm text-gray-900 mb-2">Contact Information</h4>
                <div>
                  <label className="block text-sm font-semibold text-gray-900 mb-2">Full Name</label>
                  <Input
                    type="text"
                    placeholder="Your name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Email</label>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-900 mb-2">Phone</label>
                    <Input
                      type="tel"
                      placeholder="Phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      className="w-full"
                    />
                  </div>
                </div>
                <Button
                  onClick={submitBooking}
                  disabled={submitting || !name || !email || !phone}
                  className="w-full mt-2 bg-gray-900 hover:bg-gray-800 text-white font-semibold py-2.5 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitting ? 'Submitting...' : 'Proceed to Book'}
                </Button>
              </div>
            ) : (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="text-center">
                  <div className="text-3xl mb-2">✅</div>
                  <h3 className="text-base font-bold text-green-900 mb-1">Booking Submitted!</h3>
                  <p className="text-xs text-green-700 mb-2">Booking number:</p>
                  <p className="text-lg font-bold text-green-900 mb-2">{bookingNumber}</p>
                  <p className="text-xs text-green-600">We&apos;ll contact you shortly.</p>
                </div>
              </div>
            )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RentalBooking;

